<template>
  <div class="absolute z-40 bg-primary-white w-screen">
    <BaseContainer class="py-6 space-y-4 md:space-y-6">
      <div class="border-b-2 border-border-divider w-full flex items-center">
        <input
          v-model="q"
          autofocus
          :placeholder="$t('search.searchPlaceholder')"
          class="text-[48px] md:text-[68px] font-bold leading-[78px] md:leading-[112px] outline-none w-full placeholder:text-gray-10"
          enterkeyhint="go"
          @keydown="handleKeydown"
        />
        <div
          :class="
            twJoin(
              'bg-gray-10 w-[56px] h-[56px] hidden md:flex items-center justify-center rounded-lg cursor-pointer hover:opacity-50',
              q.length > 0 && '!bg-primary-black'
            )
          "
          @click="q = ''"
        >
          <div v-if="isLoading" class="animate-spin">
            <svg
              class="scale-150"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.9375 2.87954C9.09375 3.41079 8.8125 3.94204 8.28125 4.09829C5.78125 4.84829 4 7.16079 4 9.84829C4 13.1608 6.65625 15.8483 10 15.8483C13.3125 15.8483 16 13.1608 16 9.84829C16 7.16079 14.1875 4.84829 11.6875 4.09829C11.1562 3.94204 10.875 3.41079 11.0312 2.87954C11.1875 2.34829 11.75 2.03579 12.2812 2.19204C15.5625 3.19204 18 6.22329 18 9.84829C18 14.2858 14.4062 17.8483 10 17.8483C5.5625 17.8483 2 14.2858 2 9.84829C2 6.22329 4.40625 3.19204 7.6875 2.19204C8.21875 2.03579 8.78125 2.34829 8.9375 2.87954Z"
                :fill="q.length == 0 ? '#D0D2D3' : 'white'"
              />
            </svg>
          </div>

          <svg
            v-if="q.length > 0 && !isLoading"
            width="24"
            height="23"
            viewBox="0 0 24 23"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.6484 22.4688C22.2266 22.8906 21.4531 22.8906 21.0312 22.4688L11.75 13.1172L2.39844 22.4688C1.97656 22.8906 1.20312 22.8906 0.78125 22.4688C0.359375 22.0469 0.359375 21.2734 0.78125 20.8516L10.1328 11.5L0.78125 2.21875C0.359375 1.79688 0.359375 1.02344 0.78125 0.601562C1.20312 0.179688 1.97656 0.179688 2.39844 0.601562L11.75 9.95312L21.0312 0.601562C21.4531 0.179688 22.2266 0.179688 22.6484 0.601562C23.0703 1.02344 23.0703 1.79688 22.6484 2.21875L13.2969 11.5L22.6484 20.8516C23.0703 21.2734 23.0703 22.0469 22.6484 22.4688Z"
              fill="white"
            />
          </svg>

          <svg
            v-if="q.length == 0 && !isLoading"
            width="32"
            height="28"
            viewBox="0 0 32 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M31.1484 15.2734L19.4766 27.6484C19.2656 27.9297 18.9141 28 18.6328 28C18.3516 28 18.0703 27.9297 17.8594 27.7188C17.3672 27.2969 17.3672 26.5938 17.7891 26.1719L27.6328 15.625H1.125C0.492188 15.625 0 15.1328 0 14.5703C0 14.0078 0.492188 13.375 1.125 13.375H27.6328L17.7891 2.89844C17.3672 2.47656 17.3672 1.77344 17.8594 1.35156C18.3516 0.929688 19.0547 0.929688 19.4766 1.42188L31.1484 13.7969C31.5703 14.2188 31.5703 14.8516 31.1484 15.2734Z"
              fill="#D0D2D3"
            />
          </svg>
        </div>
      </div>
      <div class="md:grid items-start grid-cols-5">
        <div class="col-start-1 col-end-4 md:pr-[112px]">
          <div
            class="flex gap-x-2 gap-y-2 overflow-scroll no-scrollbar mb-4 md:mb-0 px-5 md:px-0 -mx-5 md:mx-0 w-screen md:w-full items-start justify-start md:flex-wrap"
          >
            <NuxtLinkLocale
              v-for="(tag, index) in tags"
              :key="index"
              :to="tag.url"
              class="bg-primary-tangerine whitespace-nowrap px-4 py-3 rounded-2xl text-primary-white hover:opacity-50 cursor-pointer"
              @click="() => emit('submit')"
            >
              {{ tag.title }}
            </NuxtLinkLocale>
          </div>
          <div
            v-if="!isLoading && !isLoadingQuickLinks && !q"
            class="mt-6 space-y-3"
          >
            <NuxtLinkLocale
              v-for="(item, index) in pageData"
              :key="index"
              :to="item.primaryButtonHref"
              class="flex gap-x-2 hover:opacity-50 cursor-pointer"
              @click="() => emit('submit')"
            >
              <div
                v-if="item.tagEn"
                class="text-xs bg-black px-1 py-1 text-white"
              >
                {{ locale === 'en' ? item.tagEn : item.tagTh || item.tagEn }}
              </div>
              {{
                locale === 'en'
                  ? item.primaryButtonEn
                  : item.primaryButtonTh || item.primaryButtonEn
              }}
            </NuxtLinkLocale>
          </div>
          <div v-if="isLoading || isLoadingQuickLinks" class="mt-6 space-y-3">
            <SkeletonLoader class="w-[220px] h-7" />
            <SkeletonLoader class="w-[320px] h-7" />
            <SkeletonLoader class="w-[280px] h-7" />
            <SkeletonLoader class="w-[150px] h-7" />
          </div>
          <div v-if="!!q && !isLoading" class="mt-6 space-y-3">
            <NuxtLinkLocale
              v-for="item in result?.hits"
              :key="item.objectID"
              :to="`${getProductUrlV2(item.product_slug, item.artwork_slug)}?`"
              class="hover:opacity-50 cursor-pointer block"
              @click="
                () =>
                  handleSelectItem(
                    locale === 'en'
                      ? item.display_name_en
                      : item.display_name_th || item.display_name_en,
                    item.artist_name_en
                  )
              "
            >
              {{
                locale === 'en'
                  ? item.display_name_en
                  : item.display_name_th || item.display_name_en
              }}
            </NuxtLinkLocale>
          </div>
        </div>
        <div class="hidden md:grid col-start-4 col-end-6 grid-cols-2 gap-6">
          <NuxtLinkLocale
            v-for="featured in featuredData"
            :key="featured.titleEn"
            class="w-full hover:opacity-50"
            :to="featured.href"
            @click="emit('submit')"
          >
            <div class="relative w-full pt-[100%]">
              <div
                class="absolute top-0 left-0 w-full h-full bg-primary-white rounded-xl"
              >
                <NuxtImg
                  format="webp"
                  loading="lazy"
                  :alt="featured.titleEn?.toLowerCase() ?? ''"
                  :src="featured.imageUrl"
                  class="w-full h-full object-cover border border-border-divider rounded-xl"
                />
                <div
                  class="absolute w-6 h-6 right-0 bottom-[48px] rounded-full border-b border-border-divider -rotate-45 border-l border-l-transparent border-r border-r-transparent"
                />
                <div
                  class="absolute w-6 h-6 right-[48px] bottom-0 rounded-full border-b border-border-divider -rotate-45 border-l border-l-transparent border-r border-r-transparent"
                />
                <div
                  :class="
                    twJoin(
                      'absolute bottom-0 right-0 z-10 pt-2 pl-2 bg-primary-white rounded-tl-lg',
                      'border-t border-l border-border-divider',
                      `after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-3 after:h-3 after:bg-[radial-gradient(circle_at_100%_100%,_transparent_12px,_white_12px)] after:rotate-180 after:-translate-x-[100%]`,
                      `before:content-[''] before:absolute before:right-0 before:top-0 before:w-3 before:h-3 before:bg-[radial-gradient(circle_at_100%_100%,_transparent_12px,_white_12px)] before:rotate-180 before:-translate-y-[100%]`
                    )
                  "
                >
                  <div
                    class="w-10 h-10 bg-primary-black flex items-center justify-center rounded-lg"
                  >
                    <svg
                      width="21"
                      height="21"
                      viewBox="0 0 21 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 1.25V15.25C21 15.8125 20.5 16.25 20 16.25C19.4375 16.25 19 15.8125 19 15.25V3.6875L2.6875 20C2.5 20.1875 2.25 20.25 2 20.25C1.6875 20.25 1.4375 20.1875 1.25 20C0.875 19.625 0.875 18.9375 1.25 18.5625L17.5625 2.25H5.9375C5.4375 2.25 4.9375 1.75 5 1.25C5 0.6875 5.4375 0.25 6 0.25H20C20.5 0.25 21 0.75 21 1.25Z"
                        fill="white"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div class="bg-primary-white relative z-10 pt-4">
              {{
                locale === 'en'
                  ? featured.titleEn
                  : featured.titleTh || featured.titleEn
              }}
            </div>
          </NuxtLinkLocale>
        </div>
      </div>
    </BaseContainer>
  </div>
</template>

<script lang="ts" setup>
import { debounce } from 'lodash-es'
import { twJoin } from 'tailwind-merge'
import getProductPageUrlBySearch from '~/composables/getProductPageUrlBySearch'
import { cmsRepository } from '~/repository/cmsRepository'

const emit = defineEmits<{
  (e: 'submit'): void
}>()

const tags = [
  {
    title: 'iPhone 15 Pro Max',
    url: '/devices/iphone-15-pro-max'
  },
  {
    title: 'Typography',
    url: '/styles/typography'
  },
  {
    title: 'Pattern',
    url: '/styles/pattern'
  },
  {
    title: 'Animals Prints',
    url: '/styles/animal'
  },
  { title: 'Under 1000 THB', url: '/products?max-price=1000' }
]

const q = ref<string>('')
const isLoading = ref(false)
const isLoadingQuickLinks = ref(false)

const router = useRouter()
const localePath = useLocalePath()
const config = useRuntimeConfig()
const { on } = useEventTracking()
const { locale } = useI18n()
const { result, search } = useAlgoliaSearch<AlgoliaProductV2>(
  config.public.algoliaIndexNext
) // pass your index name as param
const route = useRoute()
const params = computed(() => route.params)

const { $serverApi } = useNuxtApp()
const cmsRepo = cmsRepository($serverApi)
const pageData = ref<
  {
    tagEn: string
    tagTh: string
    primaryButtonEn: string
    primaryButtonTh?: string
    primaryButtonHref?: string
  }[]
>([])

const { execute: refreshSearchQuickLinks } = await useAsyncData<
  {
    tagEn: string
    tagTh: string
    primaryButtonEn: string
    primaryButtonTh?: string
    primaryButtonHref?: string
  }[]
>(
  'getLandingPageCollection',
  async () => {
    const response = await cmsRepo.getWidget('search-quick-links')
    if (response.data.data.items) {
      isLoadingQuickLinks.value = false
      pageData.value = response.data.data.items
      return response.data.data.items
    }
    return null
  },
  { server: false }
)

const { data: featuredData } = await useAsyncData<
  {
    imageUrl?: string
    titleEn: string
    titleTh: string
    href?: string
  }[]
>(
  'getSearchFeatured',
  async () => {
    const response = await cmsRepo.getWidget('search-featured')
    if (response.data.data.items) return response.data.data.items
    return null
  },
  { server: false }
)

const props = defineProps<{
  isSearch?: boolean
}>()

const handleSelectItem = (name: string, artworkName?: string | null) => {
  on('select_item', {
    item_category: 'products',
    item_name: name,
    ...(artworkName && { artwork_name: artworkName })
  })
}

const debounceSearch = debounce(async (value: string) => {
  await search({
    query: value,
    requestOptions: {
      page: 0,
      hitsPerPage: 5
    }
  })
  isLoading.value = false
}, 1000)

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    isLoading.value = true
    emit('submit')
    if (q.value) {
      router.push(
        localePath(getProductPageUrlBySearch(encodeURIComponent(q.value)))
      )
    }
  }
}

watch(
  () => [q.value, props.isSearch, params.value],
  (nextQuery) => {
    if (nextQuery[1]) {
      isLoading.value = true
      debounceSearch(nextQuery[0] as string)
      refreshSearchQuickLinks()
    }
  }
)

onMounted(async () => {
  isLoading.value = true
  debounceSearch('')
  refreshSearchQuickLinks()
})
</script>
